import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Upload, X, Trash2, Pencil, Loader2, Users, Filter, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ApiContact {
  id: number;
  phone: string;
  name: string | null;
  email: string | null;
  opt_in_status: string;
  tags: string[];
  metadata: Record<string, string>;
  opt_in_at: string | null;
  opt_out_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ContactCounts {
  total: number;
  opted_in: number;
  opted_out: number;
  pending: number;
}

interface ApiSegment {
  id: number;
  name: string;
  filter_criteria: FilterCriteria;
  cached_count: number;
  created_at: string;
  updated_at: string;
}

interface FilterCriteria {
  tags?: string[];
  name_contains?: string;
  phone_prefix?: string;
  created_after?: string;
  created_before?: string;
}

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const selectClass = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const labelClass = "mb-1 block text-xs font-medium text-muted-foreground";

function ContactFormModal({
  onClose,
  editContact,
}: {
  onClose: () => void;
  editContact?: ApiContact | null;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!editContact;
  const [phone, setPhone] = useState(editContact?.phone || "");
  const [name, setName] = useState(editContact?.name || "");
  const [email, setEmail] = useState(editContact?.email || "");
  const [optInStatus, setOptInStatus] = useState(editContact?.opt_in_status || "pending");
  const [tagsInput, setTagsInput] = useState(editContact?.tags?.join(", ") || "");

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      isEdit
        ? apiFetch(`/api/contacts/${editContact.id}`, { method: "PATCH", body: JSON.stringify(data) })
        : apiFetch("/api/contacts", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
    onError: (err: Error) => alert(err.message),
  });

  const handleSubmit = () => {
    const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
    mutation.mutate({ phone, name: name || null, email: email || null, opt_in_status: optInStatus, tags });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{isEdit ? "Edit contact" : "Add contact"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Phone <span className="text-destructive">*</span></label>
            <input className={inputClass} placeholder="+51920507857" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isEdit} />
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input className={inputClass} placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input className={inputClass} placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Opt-in status</label>
            <select className={selectClass} value={optInStatus} onChange={(e) => setOptInStatus(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="opted_in">Opted in</option>
              <option value="opted_out">Opted out</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Tags (comma-separated)</label>
            <input className={inputClass} placeholder="vip, premium" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!phone.trim() || mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isEdit ? "Save" : "Add contact"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: (formData: FormData) =>
      fetch("/api/contacts/import", { method: "POST", body: formData, credentials: "same-origin" })
        .then((r) => r.json())
        .then((d) => { if (d.error) throw new Error(d.error); return d; }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      alert(`Import started: ${data.count} rows`);
      onClose();
    },
    onError: (err: Error) => alert(err.message),
  });

  const handleUpload = () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-2xl">
        <h3 className="font-semibold mb-3">Import contacts from CSV</h3>
        <p className="text-xs text-muted-foreground mb-4">
          CSV must have columns: <span className="font-mono">phone</span> (required), <span className="font-mono">name</span>, <span className="font-mono">email</span>, <span className="font-mono">tags</span>, <span className="font-mono">opt_in_status</span>
        </p>
        <input ref={fileRef} type="file" accept=".csv" className="block w-full text-sm file:mr-4 file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground" />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleUpload} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
}

function SegmentFormModal({
  onClose,
  editSegment,
  availableTags,
}: {
  onClose: () => void;
  editSegment?: ApiSegment | null;
  availableTags: string[];
}) {
  const queryClient = useQueryClient();
  const isEdit = !!editSegment;
  const [name, setName] = useState(editSegment?.name || "");
  const [selectedTags, setSelectedTags] = useState<string[]>(editSegment?.filter_criteria?.tags || []);
  const [nameContains, setNameContains] = useState(editSegment?.filter_criteria?.name_contains || "");
  const [phonePrefix, setPhonePrefix] = useState(editSegment?.filter_criteria?.phone_prefix || "");
  const [createdAfter, setCreatedAfter] = useState(editSegment?.filter_criteria?.created_after || "");
  const [createdBefore, setCreatedBefore] = useState(editSegment?.filter_criteria?.created_before || "");
  const [tagInput, setTagInput] = useState("");

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      isEdit
        ? apiFetch(`/api/segments/${editSegment.id}`, { method: "PATCH", body: JSON.stringify(data) })
        : apiFetch("/api/segments", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["segments"] });
      onClose();
    },
    onError: (err: Error) => alert(err.message),
  });

  const handleSubmit = () => {
    const filter_criteria: FilterCriteria = {};
    if (selectedTags.length > 0) filter_criteria.tags = selectedTags;
    if (nameContains.trim()) filter_criteria.name_contains = nameContains.trim();
    if (phonePrefix.trim()) filter_criteria.phone_prefix = phonePrefix.trim();
    if (createdAfter) filter_criteria.created_after = createdAfter;
    if (createdBefore) filter_criteria.created_before = createdBefore;
    mutation.mutate({ name, filter_criteria });
  };

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (t && !selectedTags.includes(t)) {
      setSelectedTags([...selectedTags, t]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const hasFilters = selectedTags.length > 0 || nameContains || phonePrefix || createdAfter || createdBefore;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-2xl" style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{isEdit ? "Edit segment" : "Create segment"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Segment name <span className="text-destructive">*</span></label>
            <input className={inputClass} placeholder="e.g. VIP Customers" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-xs font-semibold text-foreground mb-3">Filter criteria</p>
            <p className="text-[11px] text-muted-foreground mb-4">
              Only opted-in contacts matching <em>all</em> filters below will be included. Leave a filter empty to skip it.
            </p>

            <div className="space-y-3">
              <div>
                <label className={labelClass}>Tags (contact must have all selected tags)</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      className={inputClass}
                      placeholder="Type a tag and press Enter"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); }
                      }}
                      list="available-tags"
                    />
                    <datalist id="available-tags">
                      {availableTags.filter((t) => !selectedTags.includes(t)).map((t) => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => addTag(tagInput)} disabled={!tagInput.trim()}>
                    Add
                  </Button>
                </div>
                {selectedTags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selectedTags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Name contains</label>
                <input className={inputClass} placeholder="e.g. Garcia" value={nameContains} onChange={(e) => setNameContains(e.target.value)} />
              </div>

              <div>
                <label className={labelClass}>Phone prefix</label>
                <input className={inputClass} placeholder="e.g. +51" value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Created after</label>
                  <input type="date" className={inputClass} value={createdAfter} onChange={(e) => setCreatedAfter(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Created before</label>
                  <input type="date" className={inputClass} value={createdBefore} onChange={(e) => setCreatedBefore(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!name.trim() || !hasFilters || mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isEdit ? "Save segment" : "Create segment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SegmentsTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editSegment, setEditSegment] = useState<ApiSegment | null>(null);

  const { data } = useQuery({
    queryKey: ["segments"],
    queryFn: () => apiFetch<{ segments: ApiSegment[] }>("/api/segments"),
  });

  const { data: tagsData } = useQuery({
    queryKey: ["contacts", "tags"],
    queryFn: () => apiFetch<{ tags: string[] }>("/api/contacts/tags"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/segments/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["segments"] }),
  });

  const refreshMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/segments/${id}`, { method: "PATCH", body: JSON.stringify({}) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["segments"] }),
  });

  const segments = data?.segments ?? [];
  const availableTags = tagsData?.tags ?? [];

  const formatFilters = (criteria: FilterCriteria) => {
    const parts: string[] = [];
    if (criteria.tags?.length) parts.push(`Tags: ${criteria.tags.join(", ")}`);
    if (criteria.name_contains) parts.push(`Name contains "${criteria.name_contains}"`);
    if (criteria.phone_prefix) parts.push(`Phone starts with "${criteria.phone_prefix}"`);
    if (criteria.created_after) parts.push(`After ${criteria.created_after}`);
    if (criteria.created_before) parts.push(`Before ${criteria.created_before}`);
    return parts;
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Segments are dynamic groups of opted-in contacts based on filter rules. Use them to target broadcasts.
        </p>
        <Button size="sm" onClick={() => { setEditSegment(null); setShowForm(true); }}>
          <Plus className="h-4 w-4" /> Create segment
        </Button>
      </div>

      {segments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 py-12 text-center">
          <Filter className="mx-auto h-8 w-8 text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">No segments yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Create a segment to group contacts by tags, name, phone prefix, or date.</p>
          <Button size="sm" className="mt-4" onClick={() => { setEditSegment(null); setShowForm(true); }}>
            <Plus className="h-4 w-4" /> Create your first segment
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {segments.map((seg) => (
            <div key={seg.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold">{seg.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{seg.cached_count}</span> opted-in contact{seg.cached_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {formatFilters(seg.filter_criteria || {}).map((filter, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                        <Filter className="h-3 w-3" />
                        {filter}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <Button variant="ghost" size="icon" title="Refresh count" onClick={() => refreshMutation.mutate(seg.id)}>
                    <RefreshCw className={cn("h-3.5 w-3.5", refreshMutation.isPending && "animate-spin")} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { setEditSegment(seg); setShowForm(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete segment "${seg.name}"?`)) deleteMutation.mutate(seg.id); }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showForm || editSegment) && (
        <SegmentFormModal
          onClose={() => { setShowForm(false); setEditSegment(null); }}
          editSegment={editSegment}
          availableTags={availableTags}
        />
      )}
    </>
  );
}

function ContactsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState<ApiContact | null>(null);
  const [showImport, setShowImport] = useState(false);

  const { data } = useQuery({
    queryKey: ["contacts", search],
    queryFn: () => apiFetch<{ contacts: ApiContact[]; counts: ContactCounts }>(`/api/contacts?search=${encodeURIComponent(search)}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts"] }),
  });

  const contacts = data?.contacts ?? [];
  const counts = data?.counts ?? { total: 0, opted_in: 0, opted_out: 0, pending: 0 };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-xs">
          <span className="text-muted-foreground">Total: <span className="font-medium text-foreground">{counts.total}</span></span>
          <span className="text-muted-foreground">Opted in: <span className="font-medium text-green-600">{counts.opted_in}</span></span>
          <span className="text-muted-foreground">Opted out: <span className="font-medium text-red-600">{counts.opted_out}</span></span>
          <span className="text-muted-foreground">Pending: <span className="font-medium text-yellow-600">{counts.pending}</span></span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button size="sm" onClick={() => { setEditContact(null); setShowForm(true); }}>
            <Plus className="h-4 w-4" /> Add contact
          </Button>
        </div>
      </div>

      <FilterBar searchPlaceholder="Search by name, phone or email..." />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Phone</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Tags</th>
              <th className="px-4 py-3 text-left font-medium">Opt-in</th>
              <th className="px-4 py-3 text-left font-medium">Added</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contacts.map((c) => (
              <tr key={c.id} className="hover:bg-muted/40">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                      {(c.name || c.phone).split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <span className="font-medium">{c.name || c.phone}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{c.phone}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{c.email || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {c.tags.map((tag) => (
                      <span key={tag} className="inline-flex rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">{tag}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs ${
                    c.opt_in_status === "opted_in" ? "text-green-600" : c.opt_in_status === "opted_out" ? "text-destructive" : "text-yellow-600"
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      c.opt_in_status === "opted_in" ? "bg-green-500" : c.opt_in_status === "opted_out" ? "bg-destructive" : "bg-yellow-500"
                    }`} />
                    {c.opt_in_status === "opted_in" ? "Opted in" : c.opt_in_status === "opted_out" ? "Opted out" : "Pending"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-2 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditContact(c); setShowForm(true); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete ${c.name || c.phone}?`)) deleteMutation.mutate(c.id); }}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No contacts yet. Add one manually or import a CSV.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(showForm || editContact) && (
        <ContactFormModal
          onClose={() => { setShowForm(false); setEditContact(null); }}
          editContact={editContact}
        />
      )}

      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </>
  );
}

export default function Audience() {
  const [tab, setTab] = useState<"contacts" | "segments">("contacts");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audience"
        description="Your contact database — tag, segment and manage opt-in status."
      />

      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        <button
          className={cn(
            "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
            tab === "contacts" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setTab("contacts")}
        >
          <span className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Contacts
          </span>
        </button>
        <button
          className={cn(
            "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
            tab === "segments" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setTab("segments")}
        >
          <span className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Segments
          </span>
        </button>
      </div>

      {tab === "contacts" ? <ContactsTab /> : <SegmentsTab />}
    </div>
  );
}
